import { Button, Table, message, InputNumber, Form, Popconfirm, Space } from "antd";
import { EditOutlined, DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import React, { useState, useEffect } from "react";
import {
  useGetRateTypeDiscounts,
  useCreateRateTypeDiscount,
  useUpdateRateTypeDiscount,
  useDeleteRateTypeDiscount,
} from "~/services/rate-type-discounts";
import { TRateTypeDiscount, TCreateRateTypeDiscount } from "~/types/rate-type-discount";
import { RateType } from "~/utils/constants";
import { capitalize } from "~/utils";

interface RateTypeDiscountsTabProps {}

export const RateTypeDiscountsTab: React.FC<RateTypeDiscountsTabProps> = () => {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form] = Form.useForm();
  const { data: discounts, isLoading, refetch } = useGetRateTypeDiscounts();
  const createDiscount = useCreateRateTypeDiscount();
  const updateDiscount = useUpdateRateTypeDiscount();
  const deleteDiscount = useDeleteRateTypeDiscount();

  // Create a merged list showing all rate types, with defaults for missing ones
  const allRateTypes = React.useMemo(() => {
    const existingMap = new Map(discounts?.map(d => [d.rateType, d]) || []);
    return Object.values(RateType).map(rateType => {
      const existing = existingMap.get(rateType);
      if (existing) {
        return existing;
      }
      // Return a placeholder object for rate types that don't exist in DB yet
      return {
        id: `placeholder-${rateType}`,
        rateType,
        discountPercentage: 0,
        createdAt: new Date(),
        updatedAt: new Date(),
        isPlaceholder: true,
      } as TRateTypeDiscount & { isPlaceholder?: boolean };
    });
  }, [discounts]);

  const handleInitializeAll = async () => {
    if (!discounts) return;
    
    const existingTypes = new Set(discounts.map(d => d.rateType));
    const missingTypes = Object.values(RateType).filter(rt => !existingTypes.has(rt));
    
    if (missingTypes.length === 0) {
      message.info("All rate types are already initialized");
      return;
    }

    try {
      // Create all missing rate types sequentially
      for (const rateType of missingTypes) {
        await createDiscount.mutateAsync({
          rateType,
          discountPercentage: 0,
        });
      }
      message.success(`Initialized ${missingTypes.length} rate type(s) with 0% discount`);
      refetch();
    } catch (error: any) {
      message.error(`Failed to initialize rate types: ${error?.message || 'Unknown error'}`);
    }
  };

  const handleSave = async (values: { discountPercentage: number }) => {
    if (editingId) {
      // Update existing
      try {
        await updateDiscount.mutateAsync({
          id: editingId,
          data: { discountPercentage: values.discountPercentage },
        });
        message.success("Discount updated successfully");
        setEditingId(null);
        form.resetFields();
        refetch();
      } catch {
        message.error("Failed to update discount");
      }
    } else {
      message.error("No discount selected for editing");
    }
  };

  const handleEdit = async (record: TRateTypeDiscount & { isPlaceholder?: boolean }) => {
    // If it's a placeholder, create it first
    if (record.isPlaceholder) {
      try {
        const newDiscount = await createDiscount.mutateAsync({
          rateType: record.rateType,
          discountPercentage: 0,
        });
        setEditingId(newDiscount.id);
        form.setFieldsValue({ discountPercentage: 0 });
        refetch();
      } catch (error: any) {
        message.error(`Failed to create rate type discount: ${error?.message || 'Unknown error'}`);
        return;
      }
    } else {
      setEditingId(record.id);
      form.setFieldsValue({ discountPercentage: record.discountPercentage });
    }
  };

  const handleCancel = () => {
    setEditingId(null);
    form.resetFields();
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteDiscount.mutateAsync(id);
      message.success("Discount deleted successfully");
      refetch();
    } catch {
      message.error("Failed to delete discount");
    }
  };

  const columns: ColumnsType<TRateTypeDiscount> = [
    {
      title: "Rate Type",
      dataIndex: "rateType",
      key: "rateType",
      width: "30%",
      render: (rateType: RateType) => (
        <span className="font-medium">{capitalize(rateType.replace('_', ' '))}</span>
      ),
    },
    {
      title: "Discount Percentage",
      dataIndex: "discountPercentage",
      key: "discountPercentage",
      width: "40%",
      render: (discountPercentage: number, record) => {
        if (editingId === record.id) {
          return (
            <Form form={form} onFinish={handleSave} layout="inline">
              <Form.Item
                name="discountPercentage"
                rules={[
                  { required: true, message: "Please enter discount percentage" },
                  { type: "number", min: -100, max: 100, message: "Must be between -100 and 100" },
                ]}
                style={{ margin: 0, width: 200 }}
              >
                <InputNumber
                  placeholder="e.g., 10 for discount, -5 for premium"
                  min={-100}
                  max={100}
                  formatter={(value) => value !== undefined ? `${value}%` : ''}
                  parser={(value) => value!.replace('%', '')}
                  style={{ width: '100%' }}
                />
              </Form.Item>
              <Space>
                <Button type="link" htmlType="submit" size="small">
                  Save
                </Button>
                <Button type="link" onClick={handleCancel} size="small">
                  Cancel
                </Button>
              </Space>
            </Form>
          );
        }
        return (
          <span className={discountPercentage > 0 ? "text-green-600" : discountPercentage < 0 ? "text-red-600" : ""}>
            {discountPercentage > 0 ? '+' : ''}{discountPercentage}%
          </span>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: "30%",
      render: (_, record: TRateTypeDiscount & { isPlaceholder?: boolean }) => (
        <Space>
          {editingId !== record.id && (
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
              size="small"
            >
              {record.isPlaceholder ? "Create" : "Edit"}
            </Button>
          )}
          {!record.isPlaceholder && (
            <Popconfirm
              title="Delete Discount"
              description="Are you sure you want to delete this discount? It will be reset to 0%."
              onConfirm={() => handleDelete(record.id)}
              okText="Yes"
              cancelText="No"
            >
              <Button type="link" danger icon={<DeleteOutlined />} size="small">
                Delete
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  const hasPlaceholders = allRateTypes.some(rt => (rt as any).isPlaceholder);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-medium mb-2">Rate Type Discounts</h3>
          <p className="text-gray-500 mb-3">
            Set discount percentages for each client rate type. These will be automatically applied to all bookings for clients with the corresponding rate type.
          </p>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <h4 className="font-medium text-blue-900 mb-2">How It Works:</h4>
            <ul className="list-disc list-inside space-y-1 text-sm text-blue-800">
              <li><strong>Positive numbers (e.g., 10)</strong> = <span className="text-green-700 font-medium">Discount</span> - Reduces the total bill by that percentage</li>
              <li><strong>Negative numbers (e.g., -5)</strong> = <span className="text-red-700 font-medium">Premium</span> - Increases the total bill by that percentage</li>
              <li><strong>Zero (0)</strong> = No adjustment - Standard pricing applies</li>
            </ul>
            <p className="text-xs text-blue-700 mt-3 italic">
              Example: Entering "10" gives a 10% discount. Entering "-5" adds a 5% premium.
            </p>
          </div>
        </div>
        {hasPlaceholders && (
          <Button 
            type="primary" 
            icon={<PlusOutlined />} 
            onClick={handleInitializeAll}
            loading={createDiscount.isPending}
          >
            Initialize All Rate Types
          </Button>
        )}
      </div>

      <Table
        columns={columns}
        dataSource={allRateTypes}
        rowKey={(record) => record.id || `placeholder-${record.rateType}`}
        loading={isLoading}
        pagination={false}
        className="bg-white"
      />
    </div>
  );
};
